import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { projects } from '../data/projects';

function CaseSection({ number, title, children }) {
  return (
    <section className="case-section">
      <div className="case-section-label">
        <p className="eyebrow">{number} / {title}</p>
      </div>
      <div className="case-section-body">{children}</div>
    </section>
  );
}

function Architecture({ architecture }) {
  return (
    <>
      <div className="case-section-intro">
        <h2>Where each responsibility lives.</h2>
        <p>{architecture.intro}</p>
      </div>
      <section className="architecture-panel" aria-labelledby="architecture-components-title">
        <p className="eyebrow" id="architecture-components-title">Main components</p>
        <div className="architecture-list">
          {architecture.components.map((component) => (
            <div className="architecture-row" key={component.name}>
              <h3>{component.name}</h3>
              <p>{component.detail}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function Implementation({ items }) {
  return (
    <div className="case-detail-list">
      {items.map((item) => (
        <article className="case-detail" key={item.heading}>
          <h3>{item.heading}</h3>
          <p>{item.body}</p>
        </article>
      ))}
    </div>
  );
}

function KeyFlow({ steps }) {
  return (
    <ol className="flow-list" aria-label="Technical workflow">
      {steps.map((step, index) => (
        <li className="flow-step" key={step.label}>
          <span className="flow-step-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
          <div>
            <h3>{step.label}</h3>
            <p>{step.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Decisions({ decisions }) {
  return (
    <div className="decision-list">
      {decisions.map((item) => (
        <article className="decision-item" key={item.decision}>
          <h3>{item.decision}</h3>
          <dl>
            <div><dt>Why</dt><dd>{item.why}</dd></div>
            <div><dt>Trade-off</dt><dd>{item.tradeoff}</dd></div>
          </dl>
        </article>
      ))}
    </div>
  );
}

function Limits({ limits }) {
  return (
    <div className="limits-grid">
      <div>
        <h3>Current boundary</h3>
        <ul className="case-list">
          {limits.scope.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
      <div>
        <h3>Reasonable next step</h3>
        <ul className="case-list">
          {limits.next.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </div>
  );
}

export default function ProjectCaseStudy() {
  const { slug } = useParams();
  const project = projects.find((item) => item.slug === slug);

  if (!project) {
    return (
      <main className="case-page page-shell">
        <p className="eyebrow">404 / Project not found</p>
        <h1>That case study is not available.</h1>
        <Link className="text-link" to="/">Back to selected work <span aria-hidden="true">-&gt;</span></Link>
      </main>
    );
  }

  const projectType = project.type === 'backend' ? 'Backend case study' : 'Full-stack case study';

  return (
    <main className="case-page page-shell">
      <Link className="back-link" to="/">&lt;- Back to selected work</Link>

      <header className="case-hero">
        <div className="case-hero-copy">
          <div className="project-kicker">
            <p className="eyebrow">{project.number} / {project.category}</p>
            {project.status && <span className="status-badge">{project.status}</span>}
          </div>
          <h1>{project.title}</h1>
          <p className="case-summary">{project.summary}</p>
          <div className="tag-list" aria-label={`${project.title} technologies`}>
            {project.stack.map((item) => <span className="tag" key={item}>{item}</span>)}
          </div>
        </div>
        <dl className="case-meta">
          <div><dt>Project type</dt><dd>{project.projectType}</dd></div>
          <div><dt>Role</dt><dd>{project.role}</dd></div>
          <div><dt>Contribution</dt><dd>{project.contribution}</dd></div>
          <div><dt>Format</dt><dd>{projectType}</dd></div>
        </dl>
      </header>

      <div className="case-content">
        <CaseSection number="01" title="Executive summary">
          <div className="case-section-intro">
            <h2>{project.executiveSummary.intro}</h2>
          </div>
          <div className="case-three-column">
            <article><h3>{project.type === 'backend' ? 'The challenge' : 'The problem'}</h3><p>{project.executiveSummary.problem}</p></article>
            <article><h3>The solution</h3><p>{project.executiveSummary.solution}</p></article>
            <article><h3>The outcome</h3><p>{project.executiveSummary.outcome}</p></article>
          </div>
        </CaseSection>

        <CaseSection number="02" title="System architecture">
          <Architecture architecture={project.architecture} />
        </CaseSection>

        <CaseSection number="03" title="Technical implementation">
          <Implementation items={project.implementation} />
        </CaseSection>

        <CaseSection number="04" title="Key flow">
          <div className="case-section-intro">
            <h2>One important path through the system.</h2>
            <p>Read the request, validation, state change, and response in order.</p>
          </div>
          <KeyFlow steps={project.flow} />
        </CaseSection>

        <CaseSection number="05" title="Engineering decisions">
          <div className="case-section-intro">
            <h2>What the implementation chooses deliberately.</h2>
          </div>
          <Decisions decisions={project.decisions} />
        </CaseSection>

        <CaseSection number="06" title="Limits & next steps">
          <div className="case-section-intro">
            <h2>What remains outside the current boundary.</h2>
          </div>
          <Limits limits={project.limits} />
        </CaseSection>

        <CaseSection number="07" title="Project resources">
          <div className="resource-row">
            <div><p className="eyebrow">Inspect the work</p><h2>{project.title}</h2></div>
            <div className="resource-links">
              {project.resources.map((resource) => (
                <a className="text-link" href={resource.url} target="_blank" rel="noreferrer" key={resource.url}>
                  {resource.label} <span aria-hidden="true">-&gt;</span>
                </a>
              ))}
            </div>
          </div>
        </CaseSection>
      </div>

      <section className="case-cta" aria-labelledby="case-cta-title">
        <div><p className="eyebrow">{project.cta.eyebrow}</p><h2 id="case-cta-title">{project.cta.title}</h2></div>
        <a className="button button-primary" href={project.cta.href}>
          {project.cta.label} <span aria-hidden="true">-&gt;</span>
        </a>
      </section>
    </main>
  );
}
